import useTranslations from "@/shared/lang/useTranslations";

import type {
  CardStatementAccount,
  CardStatementAddress,
  CardStatementHolder,
} from "../../types/card-statement.types";

export interface StatementPartiesBlockProps {
  holder: CardStatementHolder;
  account: CardStatementAccount;
}

/** Postal order: street lines, then locality with its postal code, then country. */
const formatAddress = (address: CardStatementAddress | null): string | null => {
  if (!address) return null;
  const street = [address.address_line_1, address.address_line_2].filter(Boolean).join(", ");
  const locality = [[address.city, address.state].filter(Boolean).join(", "), address.postal_code]
    .filter(Boolean)
    .join(" ");
  return [street, locality, address.country].filter(Boolean).join(" · ") || null;
};

/** Rows drop out entirely when the value is missing, so a sparse account block flows instead of leaving holes. */
const Row = ({ label, value }: { label: string; value: string | null }) => {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-foreground">{value}</dd>
    </div>
  );
};

export const StatementPartiesBlock = ({ holder, account }: StatementPartiesBlockProps) => {
  const { t } = useTranslations("cards");

  const holderDocument =
    holder.document_number && [holder.document_type, holder.document_number].filter(Boolean).join(" ");

  return (
    <div className="grid grid-cols-2 divide-x divide-border border-b border-border">
      <section className="px-10 py-5">
        <h2 className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t("statement_section_holder")}
        </h2>
        <p className="mt-1.5 text-sm font-semibold text-foreground">
          {holder.display_name || t("statement_value_not_available")}
          {holder.source === "CARD_FALLBACK" && (
            <span aria-hidden="true" className="ml-0.5 align-super text-[8px] text-muted-foreground">
              †
            </span>
          )}
        </p>
        <dl className="mt-2 space-y-1 text-[10px] leading-snug">
          <Row label={t("statement_holder_document")} value={holderDocument || null} />
          <Row label={t("statement_holder_email")} value={holder.email} />
          <Row label={t("statement_holder_address")} value={formatAddress(holder.address)} />
        </dl>
      </section>

      <section className="px-10 py-5">
        <h2 className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t("statement_section_account")}
        </h2>
        <p className="mt-1.5 text-sm font-semibold text-foreground">
          {account.name || t("statement_value_not_available")}
        </p>
        <dl className="mt-2 space-y-1 text-[10px] leading-snug">
          <Row label={t("statement_account_tax_id")} value={account.tax_id} />
          <Row label={t("statement_account_address")} value={formatAddress(account.address)} />
        </dl>
      </section>
    </div>
  );
};
